import React, { useState } from 'react';
import { confirmDialog } from '../ConfirmDialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Key, AlertCircle, CheckCircle, Trash2, Eye, EyeOff, RefreshCw, ExternalLink, ShieldCheck } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './MyApiKeys.css';

const PROVIDER_OPTIONS = [
  { id: 'anthropic', label: 'Anthropic (Claude) — personal key', hint: 'Generate at console.anthropic.com → Settings → API Keys' },
  { id: 'vertex', label: 'Company (Google Vertex)', hint: 'JSON payload: {"project_id","region","credentials"?} — the company-billed lane. Requires roles/aiplatform.user on the project.' },
];

const LANE_ORDER = ['vertex', 'byok-vault', 'byok-body', 'platform', 'anthropic'];
const fmtUsd = (n) => `$${Number(n || 0).toFixed(n >= 1 ? 2 : 4)}`;

/**
 * BillingLanes — the observability half. One question, answered constantly:
 * WHOSE card is paying, and how much. Reads /api/auth/me/billing (the same
 * service the GAS Settings card reads, so the two UIs cannot disagree),
 * refreshes every 30s, and carries the pause switches — the GUI kill switch
 * that stops a lane before the next chat turn.
 */
function BillingLanes() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['billing-lanes'],
    queryFn: () => apiClient.get('/api/auth/me/billing'),
    refetchInterval: 30000,
  });

  const pauseMutation = useMutation({
    mutationFn: ({ provider, paused }) =>
      apiClient.patch(`/api/auth/me/api-key/${provider}`, { paused }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-lanes'] });
      queryClient.invalidateQueries({ queryKey: ['my-api-keys'] });
    },
  });

  if (isLoading) return <div className="mak__loading"><RefreshCw size={16} className="mak__spin" /><span>Loading billing lanes…</span></div>;
  if (error) return <div className="mak__error"><AlertCircle size={16} /><span>Billing lanes unavailable: {error.message}</span></div>;
  if (!data) return null;

  const windows = [['today', 'Today'], ['week', '7 days'], ['month', '30 days']];
  const lanesSeen = LANE_ORDER.filter((l) =>
    windows.some(([w]) => (data.usage?.[w] || []).some((u) => u.lane === l)));

  return (
    <section className="mak__lanes">
      <h2>Billing lanes — who is paying</h2>
      <div className={`mak__active-lane mak__active-lane--${data.active_lane}`}>
        <span className="mak__active-dot" />
        <span>
          Next chat turn runs on: <strong>{data.active_lane_label}</strong>
        </span>
      </div>

      {(data.keys || []).length > 0 && (
        <ul className="mak__list">
          {data.keys.map((k) => (
            <li key={k.provider} className="mak__item">
              <div className="mak__item-main">
                <div className="mak__item-provider">
                  {k.paused ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
                  <strong>{k.label}</strong>
                  {k.paused && <span className="mak__paused-chip">PAUSED</span>}
                </div>
                <div className="mak__item-meta">
                  <span>Fingerprint: <code>{k.fingerprint}</code></span>
                  {k.last_used_at
                    ? <span>Last served a turn {new Date(k.last_used_at).toLocaleString()}</span>
                    : <span>Never used</span>}
                </div>
              </div>
              <button
                className="mak__remove"
                onClick={() => pauseMutation.mutate({ provider: k.provider, paused: !k.paused })}
                disabled={pauseMutation.isPending}
                title={k.paused ? 'Resume this lane' : 'Stop this lane before the next chat turn'}
              >
                {k.paused ? 'Resume' : 'Pause'}
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mak__usage-tablewrap">
        <table className="mak__usage">
          <thead>
            <tr>
              <th>Lane</th>
              {windows.map(([w, label]) => <th key={w}>{label}</th>)}
            </tr>
          </thead>
          <tbody>
            {lanesSeen.length === 0 && (
              <tr><td colSpan={4} className="mak__empty">No AI usage recorded yet for your account.</td></tr>
            )}
            {lanesSeen.map((lane) => (
              <tr key={lane}>
                <td>{(data.usage.month.find((u) => u.lane === lane) || data.usage.week.find((u) => u.lane === lane) || data.usage.today.find((u) => u.lane === lane))?.label || lane}</td>
                {windows.map(([w]) => {
                  const u = (data.usage?.[w] || []).find((x) => x.lane === lane);
                  return (
                    <td key={w}>
                      {u ? <>{u.calls} calls · {fmtUsd(u.cost_usd)}</> : '—'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mak__lane-note">{data.company_lane_note}</p>
    </section>
  );
}

function MyApiKeys() {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState('anthropic');
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['my-api-keys'],
    queryFn: () => apiClient.get('/api/auth/me/api-key'),
  });

  const saveMutation = useMutation({
    mutationFn: ({ provider, api_key }) =>
      apiClient.post('/api/auth/me/api-key', { provider, api_key }),
    onSuccess: (result) => {
      setSaveSuccess(`Saved. Fingerprint: ${result.fingerprint}`);
      setSaveError(null);
      setApiKey('');
      setShowKey(false);
      queryClient.invalidateQueries({ queryKey: ['my-api-keys'] });
    },
    onError: (err) => {
      setSaveSuccess(null);
      setSaveError(err.message || 'Failed to save key');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (providerId) =>
      apiClient.del(`/api/auth/me/api-key/${providerId}`),
    onMutate: (providerId) => {
      // Optimistic — drop the key row immediately; roll back on failure.
      setSaveSuccess(null);
      setSaveError(null);
      setDeleteError(null);
      const previous = queryClient.getQueryData(['my-api-keys']);
      if (previous?.keys) {
        queryClient.setQueryData(['my-api-keys'], {
          ...previous,
          keys: previous.keys.filter(k => k.provider !== providerId),
        });
      }
      return { previous };
    },
    onError: (err, _providerId, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['my-api-keys'], ctx.previous);
      setDeleteError(err.message || 'Failed to remove key');
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['my-api-keys'] }),
  });

  const handleSave = (e) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    setSaveError(null);
    setSaveSuccess(null);
    saveMutation.mutate({ provider, api_key: apiKey.trim() });
  };

  const handleDelete = async (providerId) => {
    if (!(await confirmDialog(`Remove your ${providerId} API key? You will lose AI classification access unless a super admin grants it.`))) return;
    deleteMutation.mutate(providerId);
  };

  const existingKeys = data?.keys || [];
  const providerOption = PROVIDER_OPTIONS.find(p => p.id === provider);

  return (
    <div className="mak">
      <header className="mak__header">
        <div className="mak__title">
          <Key size={22} />
          <div>
            <h1>AI billing & keys</h1>
            <p>
              Which credential pays for your AI usage, live — and the switches that change it.
              Keys are encrypted at rest, never logged, and never shown back after save.
            </p>
          </div>
        </div>
      </header>

      <BillingLanes />

      <section className="mak__callout">
        <ShieldCheck size={18} />
        <div>
          <strong>How it works</strong>
          <ul>
            <li>Anthropic bills you directly for any AI calls made with your key. We never see your usage.</li>
            <li>Your key is encrypted with AES-256-GCM before it hits the database.</li>
            <li>If you remove your key, AI classification stops for your account immediately.</li>
            <li>A super admin can still grant you free access to the shared server key — these two paths are independent.</li>
          </ul>
        </div>
      </section>

      {isLoading && (
        <div className="mak__loading">
          <RefreshCw size={16} className="mak__spin" />
          <span>Loading your keys…</span>
        </div>
      )}

      {error && (
        <div className="mak__error">
          <AlertCircle size={16} />
          <span>{error.message}</span>
        </div>
      )}

      {!isLoading && !error && (
        <section className="mak__existing">
          <h2>Saved keys</h2>
          {deleteError && (
            <div className="mak__error">
              <AlertCircle size={16} />
              <span>{deleteError}</span>
            </div>
          )}
          {existingKeys.length === 0 ? (
            <div className="mak__empty">No keys on file. Add one below to enable AI classification for your account.</div>
          ) : (
            <ul className="mak__list">
              {existingKeys.map(k => (
                <li key={k.provider} className="mak__item">
                  <div className="mak__item-main">
                    <div className="mak__item-provider">
                      <CheckCircle size={16} />
                      <strong>{k.provider}</strong>
                    </div>
                    <div className="mak__item-meta">
                      <span>Fingerprint: <code>{k.fingerprint}</code></span>
                      <span>Added {new Date(k.created_at).toLocaleDateString()}</span>
                      {k.last_used_at && <span>Last used {new Date(k.last_used_at).toLocaleString()}</span>}
                    </div>
                  </div>
                  <button
                    className="mak__remove"
                    onClick={() => handleDelete(k.provider)}
                    disabled={deleteMutation.isPending && deleteMutation.variables === k.provider}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <section className="mak__form-section">
        <h2>{existingKeys.some(k => k.provider === provider) ? 'Replace your key' : 'Add a key'}</h2>
        <form className="mak__form" onSubmit={handleSave}>
          <label className="mak__field">
            <span>Provider</span>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              disabled={saveMutation.isPending}
            >
              {PROVIDER_OPTIONS.map(p => (
                <option key={p.id} value={p.id}>{p.label}</option>
              ))}
            </select>
          </label>

          <label className="mak__field">
            <span>API key</span>
            <div className="mak__input-row">
              {provider === 'vertex' ? (
                <textarea
                  className="mak__json"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder={'{"project_id": "jpaulsen-0875", "region": "us-east5", "credentials": { ...service account JSON... }}'}
                  rows={5}
                  disabled={saveMutation.isPending}
                  autoComplete="off"
                  spellCheck={false}
                />
              ) : (
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-ant-..."
                disabled={saveMutation.isPending}
                autoComplete="off"
                spellCheck={false}
              />
              )}
              <button
                type="button"
                className="mak__eye"
                onClick={() => setShowKey(v => !v)}
                title={showKey ? 'Hide' : 'Show'}
              >
                {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {providerOption?.hint && (
              <small>
                {providerOption.hint}
                <a
                  href="https://console.anthropic.com/settings/keys"
                  target="_blank"
                  rel="noreferrer"
                  className="mak__ext-link"
                >
                  Open console <ExternalLink size={11} />
                </a>
              </small>
            )}
          </label>

          {saveError && (
            <div className="mak__error">
              <AlertCircle size={16} />
              <span>{saveError}</span>
            </div>
          )}
          {saveSuccess && (
            <div className="mak__success">
              <CheckCircle size={16} />
              <span>{saveSuccess}</span>
            </div>
          )}

          <div className="mak__actions">
            <button
              type="submit"
              className="mak__save"
              disabled={saveMutation.isPending || !apiKey.trim()}
            >
              {saveMutation.isPending ? (
                <><RefreshCw size={14} className="mak__spin" /> Validating with {providerOption?.label}…</>
              ) : (
                <><Key size={14} /> Validate and save</>
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default MyApiKeys;
